import { useEffect } from 'react';

/**
 * SEO component that directly manages document.head for:
 * - <title>
 * - <meta> tags (description, robots, og:*, twitter:*, geo, etc.)
 * - <link rel="canonical">
 * - JSON-LD <script> tags
 *
 * This avoids known react-helmet-async + React 18 strict-mode quirks
 * where meta and script tags occasionally fail to inject on route change.
 *
 * Every tag managed here is marked with data-seo="reclaim" so that on
 * unmount or route change, we can cleanly remove our own tags without
 * touching the static tags shipped in public/index.html.
 */

const MANAGED_ATTR = 'data-seo';
const MANAGED_VALUE = 'reclaim';

const removeManagedTags = () => {
  document.querySelectorAll(`[${MANAGED_ATTR}="${MANAGED_VALUE}"]`).forEach((el) => el.remove());
};

const setOrReplaceMeta = (selector, attrs) => {
  // Remove any existing static tag with this selector (so our content wins)
  const existing = document.head.querySelector(selector);
  if (existing && existing.getAttribute(MANAGED_ATTR) !== MANAGED_VALUE) {
    existing.remove();
  }
  const tag = document.createElement('meta');
  Object.entries(attrs).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') tag.setAttribute(k, String(v));
  });
  tag.setAttribute(MANAGED_ATTR, MANAGED_VALUE);
  document.head.appendChild(tag);
};

const setOrReplaceLink = (rel, href) => {
  const existing = document.head.querySelector(`link[rel="${rel}"]`);
  if (existing && existing.getAttribute(MANAGED_ATTR) !== MANAGED_VALUE) {
    existing.remove();
  }
  const tag = document.createElement('link');
  tag.setAttribute('rel', rel);
  tag.setAttribute('href', href);
  tag.setAttribute(MANAGED_ATTR, MANAGED_VALUE);
  document.head.appendChild(tag);
};

const appendJsonLd = (data) => {
  const tag = document.createElement('script');
  tag.setAttribute('type', 'application/ld+json');
  tag.setAttribute(MANAGED_ATTR, MANAGED_VALUE);
  tag.textContent = JSON.stringify(data);
  document.head.appendChild(tag);
};

const SEO = ({
  title,
  description,
  keywords = '',
  ogImage = '/images/hero-technician.webp',
  ogType = 'website',
  schema = null,
  canonical = '',
  noindex = false,
  article = null,
}) => {
  const siteUrl = 'https://www.reclaimpestcontrol.com';
  const BRAND_SUFFIX = '| Reclaim Pest Control';
  const fullTitle = title
    ? (title.includes('Reclaim Pest Control') ? title : `${title} ${BRAND_SUFFIX}`)
    : 'Reclaim Pest Control | NYC Pest Control & Exterminator Services';
  const finalCanonical = canonical || (typeof window !== 'undefined' ? window.location.href : siteUrl);
  const imageUrl = ogImage && ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;
  const robotsContent = noindex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  useEffect(() => {
    // 1. Title
    document.title = fullTitle;

    // 2. Remove previously managed tags to avoid duplicates on route change
    removeManagedTags();

    // 3. Primary meta tags
    setOrReplaceMeta('meta[name="title"]', { name: 'title', content: fullTitle });
    if (description) {
      setOrReplaceMeta('meta[name="description"]', { name: 'description', content: description });
    }
    if (keywords) {
      setOrReplaceMeta('meta[name="keywords"]', { name: 'keywords', content: keywords });
    }
    setOrReplaceLink('canonical', finalCanonical);

    // 4. Robots
    setOrReplaceMeta('meta[name="robots"]', { name: 'robots', content: robotsContent });
    setOrReplaceMeta('meta[name="googlebot"]', { name: 'googlebot', content: robotsContent });
    setOrReplaceMeta('meta[name="bingbot"]', { name: 'bingbot', content: noindex ? 'noindex, nofollow' : 'index, follow' });

    // 5. Open Graph
    setOrReplaceMeta('meta[property="og:type"]', { property: 'og:type', content: ogType });
    setOrReplaceMeta('meta[property="og:url"]', { property: 'og:url', content: finalCanonical });
    setOrReplaceMeta('meta[property="og:title"]', { property: 'og:title', content: fullTitle });
    if (description) {
      setOrReplaceMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    }
    setOrReplaceMeta('meta[property="og:image"]', { property: 'og:image', content: imageUrl });
    setOrReplaceMeta('meta[property="og:image:width"]', { property: 'og:image:width', content: '1200' });
    setOrReplaceMeta('meta[property="og:image:height"]', { property: 'og:image:height', content: '630' });
    setOrReplaceMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: title || 'Reclaim Pest Control' });
    setOrReplaceMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'Reclaim Pest Control' });
    setOrReplaceMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'en_US' });

    // 6. Article-specific OG
    if (article) {
      if (article.publishedDate) setOrReplaceMeta('meta[property="article:published_time"]', { property: 'article:published_time', content: article.publishedDate });
      if (article.modifiedDate || article.publishedDate) setOrReplaceMeta('meta[property="article:modified_time"]', { property: 'article:modified_time', content: article.modifiedDate || article.publishedDate });
      if (article.author) setOrReplaceMeta('meta[property="article:author"]', { property: 'article:author', content: article.author });
      if (article.category) setOrReplaceMeta('meta[property="article:section"]', { property: 'article:section', content: article.category });
      if (Array.isArray(article.tags)) {
        article.tags.forEach((tag) => {
          const m = document.createElement('meta');
          m.setAttribute('property', 'article:tag');
          m.setAttribute('content', String(tag));
          m.setAttribute(MANAGED_ATTR, MANAGED_VALUE);
          document.head.appendChild(m);
        });
      }
    }

    // 7. Twitter
    setOrReplaceMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    setOrReplaceMeta('meta[name="twitter:url"]', { name: 'twitter:url', content: finalCanonical });
    setOrReplaceMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: fullTitle });
    if (description) {
      setOrReplaceMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    }
    setOrReplaceMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: imageUrl });
    setOrReplaceMeta('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt', content: title || 'Reclaim Pest Control' });

    // 8. Geo for local SEO
    setOrReplaceMeta('meta[name="geo.region"]', { name: 'geo.region', content: 'US-NY' });
    setOrReplaceMeta('meta[name="geo.placename"]', { name: 'geo.placename', content: 'New York City' });
    setOrReplaceMeta('meta[name="geo.position"]', { name: 'geo.position', content: '40.7128;-74.0060' });
    setOrReplaceMeta('meta[name="ICBM"]', { name: 'ICBM', content: '40.7128, -74.0060' });

    // 9. JSON-LD schemas
    if (schema) {
      const items = Array.isArray(schema) ? schema : [schema];
      items.forEach((s) => {
        if (s) appendJsonLd(s);
      });
    }

    // Cleanup on unmount: remove our managed tags
    return () => {
      removeManagedTags();
    };
  }, [title, description, keywords, ogImage, ogType, canonical, noindex, JSON.stringify(schema), JSON.stringify(article)]);

  return null;
};

export default SEO;
